class PriceRenderer extends HTMLElement {
  static #symbols = {};
  static #defaults = {};
  #container;
  #observer;
  #step = null;
  #quantityValid = true;
  #quantityEl = null;
  #onQuantityInput = null;
  #onQuantityChange = null;

  static get observedAttributes() {
    return ["symbols", "lock"];
  }

  constructor() {
    super();

    this.setAttribute("data-property", "_p");

    const shadow = this.attachShadow({ mode: "closed" });
    const container = document.createElement("div");
    this.#container = container;
    shadow.appendChild(container);

    this.#calculate.bind(this);
  }

  connectedCallback() {
    this.lock(true);

    /*
      Minimum production width (Flex belts): when the PDP renders a
      <wc-min-order min="…" data-property="_step"> we drive the theme's
      <quantity-input> (step / min / value) from the chosen width and keep
      `_step` in sync. Everything is optional: a missing <wc-min-order> or
      a missing quantity field must never throw here.
    */
    if (this.minOrder) {
      this.#bindQuantity();
    }

    this.#observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes") {
          this.#container.innerText = this.#calculate();
          const variantId = parseInt(mutation.target.value);
          const variant = window.meta?.product?.variants?.find(
            (variant) => variant.id === variantId
          );
          if (variant) {
            PriceRenderer.modifySymbol("p", variant.price);
          }
        }
      });
    });

    /* new input field for variant id (old theme: `.js-variant-id`) */
    const variantInput = document.querySelector(".product-variant-id");
    if (variantInput) {
      this.#observer.observe(variantInput, { attributes: true });
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#unbindQuantity();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "symbols") {
      if (this.list) {
        try {
          PriceRenderer.modifySymbol(
            "p",
            this.list.find((d) =>
              Object.keys(PriceRenderer.#defaults).reduce(
                (result, key) =>
                  result && parseFloat(d[key]) === PriceRenderer.#symbols[key]
                    ? true
                    : false,
                true
              )
            ).p,
            false
          );
        } catch (e) {
          PriceRenderer.modifySymbol(
            "p",
            this.list.find((d) =>
              Object.keys(PriceRenderer.#defaults).reduce(
                (result, key) =>
                  result &&
                  (parseFloat(d[key]) === PriceRenderer.#symbols[key] ||
                    isNaN(parseFloat(d[key])))
                    ? true
                    : false,
                true
              )
            )?.p ||
              this.list.find((d) =>
                Object.keys(PriceRenderer.#defaults).reduce((result, key) => {
                  return result &&
                    (parseFloat(d[key]) === PriceRenderer.#defaults[key] ||
                      isNaN(parseFloat(d[key])))
                    ? true
                    : false;
                }, true)
              )?.p ||
              0,
            false
          );
        }
      }
      const price = this.#calculate();
      if (price && !isNaN(price)) {
        this.value = Math.ceil(price * 100) / 100;
        this.#container.innerText = Shopify.formatMoney(
          this.value * 100,
          document.body.dataset.moneyFormat
        );
        this.release(true);
      } else {
        this.lock(true);
        const price = this.#calculate(
          PriceRenderer.#fillSymbols(
            PriceRenderer.#fillSymbols(
              this.getAttribute("equation"),
              PriceRenderer.#symbols
            ),
            PriceRenderer.#defaults
          )
        );
        this.#container.innerText =
          this.prefix +
          Shopify.formatMoney(
            Math.ceil(price * 100),
            document.body.dataset.moneyFormat
          );
      }
      this.#applyStep();
      this.dispatchEvent(
        new CustomEvent("change", {
          bubbles: true,
          detail: {
            value: this.value
          }
        })
      );
    } else if (name === "lock") {
      this.#applyButtonState();
    }
  }

  get prefix() {
    return this.getAttribute("default-prefix");
  }

  get equation() {
    return PriceRenderer.#fillSymbols(this.getAttribute("equation"));
  }

  get list() {
    return window[this.getAttribute("list")];
  }

  /*
    The section that hosts this renderer. On the configurable PDP the
    configurator (this element), the buy-buttons block (quantity field,
    `button[name="add"]`, <wc-min-order>) and the legacy
    `form.js-add-to-cart-form` all live in the same Shopify section, so we
    scope every lookup to it and only fall back to the document.
  */
  get #sectionRoot() {
    return this.closest("[id^='shopify-section-']") || document;
  }

  get minOrder() {
    return (
      this.#sectionRoot.querySelector("wc-min-order") ||
      document.querySelector("wc-min-order")
    );
  }

  /* Minimum production width in mm (attribute `min` of <wc-min-order>). */
  get minOrderWidth() {
    const value = parseFloat(this.minOrder?.getAttribute("min"));
    return value > 0 ? value : NaN;
  }

  /*
    The theme's quantity field. It is NOT inside `form.js-add-to-cart-form`
    (the configurator form) but belongs to `form.main-product-form` via its
    `form` attribute and lives in <quantity-input>, so `.js-qty-num` from the
    old theme never matches. Prefer the input bound to the product form.
  */
  get quantity() {
    const root = this.#sectionRoot;
    return (
      root.querySelector(
        "quantity-input input.quantity__input[name='quantity'][form]"
      ) ||
      root.querySelector("quantity-input input[name='quantity']") ||
      root.querySelector("input[name='quantity']") ||
      document.querySelector("input.quantity__input[name='quantity']")
    );
  }

  /* The add-to-cart button of the theme's product form. */
  get addToCartButton() {
    const root = this.#sectionRoot;
    return (
      root.querySelector("form.main-product-form button[name='add']") ||
      root.querySelector("button[name='add']") ||
      document.querySelector("form.main-product-form button[name='add']") ||
      document.querySelector(".js-add-to-cart")
    );
  }

  /* Current `_step` (null while the width is unknown or no <wc-min-order>). */
  get step() {
    return this.#step;
  }

  /* false while the quantity is not a valid multiple of `_step`. */
  get quantityValid() {
    return this.#quantityValid;
  }

  get lockState() {
    return !this.hardLock && !this.hasAttribute("lock");
  }

  lock(hardLock = false) {
    if (hardLock) {
      this.hardLock = hardLock;
    }
    this.setAttribute("lock", "");
  }
  static lock() {
    PriceRenderer.instances.forEach((d) => d.lock(true));
  }

  release(hardLock = false) {
    if (hardLock) {
      this.hardLock = false;
    }
    if (!this.hardLock) {
      this.removeAttribute("lock");
    }
  }

  static release() {
    PriceRenderer.instances.forEach((d) => d.release(true));
  }

  static get instances() {
    return Array.from(document.querySelectorAll("price-renderer"));
  }

  static get valid() {
    return PriceRenderer.symbols.reduce(
      (a, b) => a && PriceRenderer.getSymbol(b),
      true
    );
  }

  static get symbols() {
    return Object.keys(this.#defaults);
  }

  static getSymbol(symbol) {
    return this.#symbols[symbol];
  }

  static #fillSymbols(equation, defaults = {}) {
    return Object.entries(this.#symbols).reduce(
      (string, [key, value]) => string.replace(new RegExp(key, "g"), value),
      Object.entries(defaults).reduce(
        (string, [key, value]) => string.replace(new RegExp(key, "g"), value),
        equation
      )
    );
  }

  static registerSymbol(symbol, value) {
    this.#defaults[symbol] = value;
  }

  static modifySymbol(symbol, value, rerender = true) {
    this.#symbols[symbol] = value;
    rerender &&
      document
        .querySelectorAll("price-renderer")
        .forEach((d) => d.setAttribute("symbols", ""));
  }

  #bindQuantity() {
    const quantity = this.quantity;
    if (!quantity || quantity === this.#quantityEl) return;
    this.#unbindQuantity();
    this.#quantityEl = quantity;
    this.#onQuantityInput = () => {
      this.#validateQuantity();
    };
    this.#onQuantityChange = () => {
      this.#snapQuantity();
      this.#validateQuantity();
    };
    quantity.addEventListener("input", this.#onQuantityInput);
    quantity.addEventListener("change", this.#onQuantityChange);
  }

  #unbindQuantity() {
    if (!this.#quantityEl) return;
    this.#quantityEl.removeEventListener("input", this.#onQuantityInput);
    this.#quantityEl.removeEventListener("change", this.#onQuantityChange);
    this.#quantityEl = null;
  }

  /*
    Backend contract: `_step` must be exactly floor(min_order_width / Breite)
    and the quantity a multiple of it. We write that exact value to
    <wc-min-order> (as `.value` and as `value` attribute so the property
    collection in buy-buttons-configurable.liquid can read either) and drive
    the quantity field with max(1, step) so the stepper stays usable.
  */
  #applyStep() {
    const minOrder = this.minOrder;
    const width = parseFloat(PriceRenderer.#symbols.b);
    const minWidth = this.minOrderWidth;
    if (!minOrder || !(width > 0) || !(minWidth > 0)) return;

    const rawStep = Math.floor(minWidth / width);
    const step = Math.max(1, rawStep);
    const stepChanged = step !== this.#step;
    this.#step = step;

    minOrder.value = rawStep;
    minOrder.setAttribute("value", rawStep);
    minOrder.setAttribute("data-width", width);

    /* the quantity field may be rendered after us (defer order) */
    this.#bindQuantity();
    const quantity = this.quantity;
    if (quantity) {
      quantity.step = step;
      quantity.min = step;
      quantity.setAttribute("data-min", step);
      const current = parseInt(quantity.value, 10);
      const base = Number.isInteger(current) ? current : 1;
      const next = Math.max(step, Math.ceil(base / step) * step);
      if (stepChanged || next !== current) {
        quantity.value = next;
        quantity.closest("quantity-input")?.validateQtyRules?.();
      }
    }

    this.#renderHint(width, step);
    this.#validateQuantity();
  }

  /* Round a typed quantity up to the next valid multiple of the step. */
  #snapQuantity() {
    const quantity = this.quantity;
    const step = this.#step;
    if (!quantity || !(step > 0) || !this.minOrder) return;
    const value = parseInt(quantity.value, 10);
    const next =
      !Number.isInteger(value) || value < step
        ? step
        : Math.ceil(value / step) * step;
    if (next !== value) {
      quantity.value = next;
      quantity.closest("quantity-input")?.validateQtyRules?.();
    }
  }

  #validateQuantity() {
    const quantity = this.quantity;
    const step = this.#step;
    let valid = true;
    if (quantity && step > 0 && this.minOrder) {
      const value = parseInt(quantity.value, 10);
      valid = Number.isInteger(value) && value >= step && value % step === 0;
    }
    this.#quantityValid = valid;

    if (quantity && this.minOrder) {
      quantity.setAttribute("aria-invalid", valid ? "false" : "true");
    }
    const error = this.#sectionRoot.querySelector("[data-reib-min-order-error]");
    if (error) {
      if (valid) {
        error.hidden = true;
      } else {
        error.textContent = (error.getAttribute("data-template") || "").replace(
          /\{step\}/g,
          step
        );
        error.hidden = false;
      }
    }

    this.#applyButtonState();
    /*
      Always notify, not only on a flip: the ATC activator in
      wc-components-pdp.liquid listens to this and has the final say over
      the button, exactly like after our "change" event.
    */
    this.dispatchEvent(
      new CustomEvent("quantity-validity", {
        bubbles: true,
        detail: { valid, step }
      })
    );
    return valid;
  }

  #renderHint(width, step) {
    const hint = this.#sectionRoot.querySelector("[data-reib-min-order-hint]");
    if (!hint) return;
    const template = hint.getAttribute("data-template") || "";
    hint.textContent = template
      .replace(/\{width\}/g, width)
      .replace(/\{step\}/g, step);
    hint.hidden = template === "";
  }

  /*
    Single place that touches the button's `disabled` attribute from this
    component: locked while the price is invalid (`lock`) or while the
    quantity is not a multiple of `_step`. The ATC activator in
    wc-components-pdp.liquid re-evaluates after our "change" /
    "quantity-validity" events and also consults `quantityValid`.
  */
  #applyButtonState() {
    const button = this.addToCartButton;
    if (!button) return;
    if (this.lockState && this.#quantityValid) {
      button.removeAttribute("disabled");
    } else {
      button.setAttribute("disabled", "true");
    }
  }

  #calculate(equation = this.equation) {
    let tokens =
      equation.replace(/ /g, "").match(/[+\-\*\/]*(\d+\.?\d*|\w)/g) || [];
    // console.log("___start___");
    return (
      tokens.reduce((result) => {
        // console.log(tokens);
        let nextIndex = tokens.findIndex(
          (d, i) =>
            !isNaN(parseFloat(d) || d.slice(i <= 0 ? 0 : 1)) &&
            isNaN(d) &&
            (d?.startsWith("*") || d?.startsWith("/"))
        );
        let next;
        let prev;
        if (nextIndex >= 0) {
          next = tokens[nextIndex];
          prev = parseFloat(
            tokens[nextIndex - 1].slice(nextIndex <= 1 ? 0 : 1)
          );
          let result;
          switch (next) {
            case next.startsWith("*") && next:
              result = tokens[nextIndex - 1] = `${tokens[nextIndex - 1].slice(
                0,
                nextIndex <= 1 ? 0 : 1
              )}${prev * parseFloat(next.slice(1))}`;
              tokens = tokens.filter((d, i) => i !== nextIndex);
              break;
            case next.startsWith("/") && next:
              result = tokens[nextIndex - 1] = `${tokens[nextIndex - 1].slice(
                0,
                nextIndex <= 1 ? 0 : 1
              )}${prev / parseFloat(next.slice(1))}`;
              tokens = tokens.filter((d, i) => i !== nextIndex);
              break;
            default:
              break;
          }
          return result;
        }
        nextIndex = tokens.findIndex(
          (d, i) =>
            d.slice(i <= 0 ? 0 : 1) &&
            (d?.startsWith("+") || d?.startsWith("-"))
        );
        next;
        prev;
        if (nextIndex >= 0) {
          next = tokens[nextIndex];
          prev = parseFloat(
            tokens[nextIndex - 1].slice(nextIndex <= 1 ? 0 : 1)
          );
          let result;
          switch (next) {
            case next.startsWith("+") && next:
              result = tokens[nextIndex - 1] = `${tokens[nextIndex - 1].slice(
                0,
                nextIndex <= 1 ? 0 : 1
              )}${prev + parseFloat(next.slice(1))}`;
              tokens = tokens.filter((d, i) => i !== nextIndex);
              break;
            case next.startsWith("-") && next:
              result = tokens[nextIndex - 1] = `${tokens[nextIndex - 1].slice(
                0,
                nextIndex <= 1 ? 0 : 1
              )}${prev - parseFloat(next.slice(1))}`;
              tokens = tokens.filter((d, i) => i !== nextIndex);
              break;
            default:
              break;
          }
          let number = parseFloat(result);
          if (isNaN(number)) {
            result = NaN;
          } else {
            result = number;
          }
          return result;
        }
        return result;
      }, 0.0) || ""
    );
  }
}

customElements.define("price-renderer", PriceRenderer);
